import { gql } from '@apollo/client';
import IconCloud from '@apollo/icons/default/IconCloud.svg';
import IconCopy from '@apollo/icons/default/IconCopy.svg';
import IconHistory from '@apollo/icons/default/IconHistory.svg';
import IconSatellite1 from '@apollo/icons/default/IconSatellite1.svg';
import { Heading, IconButton } from '@apollo/orbit';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { pluckCloudRouterEndpoint } from 'src/app/graph/cloudSettingsPage/helpers/pluckCloudRouterEndpoint';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { committerDisplayName } from 'src/app/shared/commiterDisplayName';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { FavoriteButton } from 'src/components/favoriteButton/FavoriteButton';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import { useHandleFavoriteVariant } from 'src/hooks/useHandleFavoriteVariant';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

export const GraphVariantRouterFragment = gql`
  fragment GraphVariantRouterFragment on GraphVariant {
    id
    router {
      id
      routerVersion {
        version
        status
      }
      constants {
        durationBeforeSleepSecs
        durationBeforeDeleteSecs
      }
      nextStatus
      status
      shard {
        id
        status
        tier
        region {
          code
          country
          name
          provider
        }
      }
      endpoints {
        custom
        default
        defaultEnabled
        primary
      }
    }
  }
`;

export const GraphVariantOrderFragment = gql`
  fragment GraphVariantOrderFragment on GraphVariant {
    id
    latestLaunch {
      id
      createdAt
      status
    }
    latestApprovedLaunch {
      id
      order {
        ... on Order {
          completionPercentage
          id
          orderType
          status
          updatedAt
          reason
        }
        ... on OrderDoesNotExistError {
          tryAgainSeconds
        }
      }
      orders {
        id
        completionPercentage
        orderType
        status
        updatedAt
        reason
      }
    }
  }
`;

export const GraphHeaderDataServiceFragment = gql`
  fragment GraphHeaderDataServiceFragment on Service {
    accountId
    id
    graphType
    variant(name: $graphVariant) {
      id
      url
      isFavoriteOfCurrentUser
      activeSchemaPublish {
        id
        createdAt
        gitContext {
          committer
        }
        schema {
          hash
          createdAt
          typeCount
          fieldCount
        }
      }
    }
  }
`;

export const GraphHeaderData = ({
  graph,
  graphRef,
  isCloudSupergraph,
  router,
  lastUpdateAt,
  className,
}: {
  graph: GraphQLTypes.GraphHeaderDataServiceFragment;
  graphRef: GraphRef;
  isCloudSupergraph: boolean;
  router?: GraphQLTypes.GraphVariantRouterFragment['router'];
  latestLaunch?: GraphQLTypes.GraphVariantOrderFragment['latestLaunch'];
  latestApprovedLaunch?: GraphQLTypes.GraphVariantOrderFragment['latestApprovedLaunch'];
  lastUpdateAt: Timestamp['output'] | undefined;
  className?: string;
}) => {
  const location = useLocation();
  const { graphId, graphVariant } = graphRef;
  const isFavorite = graph.variant?.isFavoriteOfCurrentUser ?? false;
  const { handleFavorite } = useHandleFavoriteVariant();

  const activeSchemaPublish = graph.variant?.activeSchemaPublish;
  const typeCount = activeSchemaPublish?.schema?.typeCount;
  const fieldCount = activeSchemaPublish?.schema?.fieldCount;

  const version = activeSchemaPublish?.schema?.hash;
  const committerName = committerDisplayName(
    activeSchemaPublish?.gitContext?.committer || '',
  );

  const variantUrl: string | undefined =
    (isCloudSupergraph && pluckCloudRouterEndpoint(router)) ||
    // note: this following clause is executed when pluckCloudRouterEndpoint returns
    // undefined (e.g. the router is DELETED)
    graph.variant?.url ||
    undefined;

  // Use latest update with a "change" if there is one else use the current
  // publishes created at time.
  const lastUpdateTimestamp = lastUpdateAt
    ? new Date(lastUpdateAt)
    : activeSchemaPublish?.createdAt
      ? new Date(activeSchemaPublish?.createdAt)
      : null;

  return (
    <div className={classnames(className, 'w-full shrink')}>
      <div className="flex items-center">
        <Tooltip
          label="👈 This is your Graph Ref. Click to copy."
          clickedLabel="Copied"
          placement="right"
        >
          <Heading
            size="3xl"
            as="h1"
            className="cursor-pointer"
            onClick={() => {
              copy(`${graphRef.graphId}@${graphRef.graphVariant}`);
            }}
          >
            {graphRef.graphId}@{graphRef.graphVariant}
          </Heading>
        </Tooltip>
        <FavoriteButton
          className="h-6 w-8"
          isFavorite={isFavorite}
          onClick={() => {
            handleFavorite({
              serviceId: graphId,
              favorite: !isFavorite,
              variantName: graphVariant,
              variantId: graph.variant?.id ?? '',
            });
          }}
        />
      </div>
      <div className="flex items-center">
        <div className="mt-2 flex flex-col items-start gap-1">
          <div className="flex items-center text-secondary">
            <div className="flex items-center">
              {isCloudSupergraph ? (
                <IconCloud className="mr-2 mt-1 size-4 min-h-4 min-w-4 self-start text-icon-primary" />
              ) : (
                <IconSatellite1 className="mr-2 mt-1 size-4 self-start text-icon-primary" />
              )}
              {variantUrl && (
                <ClickableText
                  as="a"
                  href={variantUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {variantUrl}
                </ClickableText>
              )}
            </div>
            {variantUrl ? (
              <Tooltip
                label="Copy variant url"
                clickedLabel="Copied"
                placement="right"
              >
                <IconButton
                  className="mx-2"
                  variant="secondary"
                  icon={<IconCopy />}
                  onClick={() => copy(variantUrl)}
                  aria-label="Copy variant url"
                />
              </Tooltip>
            ) : (
              <span>
                This graph doesn't have an endpoint yet
                <PermissionGuard
                  graphId={graphId}
                  graphVariant={graphVariant}
                  graphVariantPermissions="canManageExplorerSettings"
                  fallback={null}
                >
                  <span> - add one in </span>

                  <ClickableText
                    className="text-link"
                    as={
                      <Link
                        to={setOverlayVisibleLocation({
                          location,
                          overlayName: Config.modals.connectionSettings,
                        })}
                        onClick={() => {
                          /* eslint-disable @typescript-eslint/naming-convention */
                          ampli.updateEndpointClicked({
                            graph_ref: `${graphRef?.graphId}@${graphRef?.graphVariant}`,
                            location: 'variant_homepage',
                          });
                          /* eslint-enable @typescript-eslint/naming-convention */
                        }}
                      />
                    }
                  >
                    {' '}
                    Connection Settings.
                  </ClickableText>
                </PermissionGuard>
              </span>
            )}
          </div>

          {lastUpdateTimestamp && (
            <div className="mt-1.5 flex items-center whitespace-pre-wrap text-secondary">
              <IconHistory className="text-icon-primary" />
              <span className="ml-2">
                <LocalTimestamp value={lastUpdateTimestamp} />
              </span>
              {committerName && committerName !== 'Unknown' && (
                <span>
                  {` by `}
                  <span className="font-semibold">{committerName}</span>
                </span>
              )}
              {version && (
                <>
                  <span className="mx-1.5">&bull;</span>
                  <span className="text-base">{version.substr(0, 6)}</span>
                </>
              )}
            </div>
          )}
        </div>
        <div className="ml-auto flex">
          {typeCount !== 0 && fieldCount !== 0 && (
            <>
              <div className="flex flex-col items-center">
                <span className="text-2xl">{typeCount}</span>
                <span className="text-center text-sm text-secondary">
                  types
                </span>
              </div>
              <div className="ml-7 flex flex-col items-center">
                <span className="text-2xl">{fieldCount}</span>
                <span className="text-center text-sm text-secondary">
                  fields
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
